import { brandColors } from '@config/brandTokens';
import { isReleaseSport } from '@config/releaseProfile';

/** Admin nav release tone — visible in sidebar, nothing hidden. */
export type AdminNavReleaseTone = 'v1' | 'future' | 'neutral';

export interface AdminNavToneStyle {
  color: string;
  hoverBg: string;
  selectedBg: string;
  border: string;
  label: string;
}

export const ADMIN_NAV_TONES: Record<AdminNavReleaseTone, AdminNavToneStyle> = {
  v1: {
    color: brandColors.orange,
    hoverBg: 'rgba(232, 88, 11, 0.08)',
    selectedBg: 'rgba(232, 88, 11, 0.14)',
    border: brandColors.orange,
    label: 'EncycGames 1.0',
  },
  future: {
    color: brandColors.slate,
    hoverBg: 'rgba(70, 80, 105, 0.07)',
    selectedBg: 'rgba(70, 80, 105, 0.12)',
    border: '#94a3b8',
    label: 'Future release',
  },
  neutral: {
    color: brandColors.navy,
    hoverBg: 'rgba(17, 23, 57, 0.06)',
    selectedBg: 'rgba(74, 111, 235, 0.1)',
    border: brandColors.blue,
    label: 'Platform',
  },
};

const V1_ADMIN_VIEWS = new Set([
  'players',
  'coaches',
  'academies',
  'contest-catalog',
  'subscriptions',
  'customer-ops',
  'support-tickets',
  'careers-applications',
  'sponsor-leads',
]);

export function getAdminNavTone(view: string): AdminNavReleaseTone {
  if (V1_ADMIN_VIEWS.has(view)) return 'v1';
  if (view === 'dashboard' || view === 'admin-user-management') return 'neutral';
  return 'future';
}

export function adminNavToneForSportModule(sport: string): AdminNavReleaseTone {
  return isReleaseSport(sport) ? 'future' : 'future';
}

export function adminTopNavButtonSx(
  tone: AdminNavReleaseTone,
  selected: boolean,
  collapsed: boolean,
  accentColor?: string,
) {
  const t = ADMIN_NAV_TONES[tone];
  const color = accentColor || t.color;
  return {
    color: selected ? color : tone === 'future' ? brandColors.slate : brandColors.navy,
    borderLeft: selected ? `3px solid ${color}` : '3px solid transparent',
    ml: collapsed ? 0 : 0.5,
    mr: collapsed ? 0 : 0.5,
    borderRadius: '0 10px 10px 0',
    py: 0.85,
    '&:hover': { bgcolor: t.hoverBg },
    '&.Mui-selected': {
      bgcolor: t.selectedBg,
      color,
      '&:hover': { bgcolor: t.selectedBg },
    },
  };
}

export function adminChildNavButtonSx(tone: AdminNavReleaseTone, depth: number) {
  const t = ADMIN_NAV_TONES[tone];
  const pl = 2 + depth * 1.75;
  return {
    pl,
    ml: 1.25,
    mr: 0.75,
    py: 0.55,
    minHeight: 36,
    borderLeft: '3px solid transparent',
    borderRadius: '0 8px 8px 0',
    color: tone === 'future' ? '#64748b' : brandColors.navy,
    bgcolor: 'transparent',
    '&:hover': { bgcolor: t.hoverBg },
    '&.Mui-selected': {
      borderLeft: `3px solid ${t.border}`,
      bgcolor: t.selectedBg,
      color: t.color,
      '&:hover': { bgcolor: t.selectedBg },
    },
    '&.Mui-disabled': { opacity: 0.55 },
  };
}

export function adminSectionHeaderSx(tone: AdminNavReleaseTone) {
  const t = ADMIN_NAV_TONES[tone];
  return {
    pl: 2.5,
    py: 0.65,
    color: t.color,
    '&:hover': { bgcolor: t.hoverBg },
  };
}

export const ADMIN_SIDEBAR_BG = brandColors.ice;
export const ADMIN_SIDEBAR_BORDER = '#e2e8f0';
